import React, { useState } from 'react';
import styled from '@emotion/native';
import { BodyText, H3 } from '@/shared/components/StyledText';
import { green, lightGreen, mint, sand, white } from '@/shared/styles/colors';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { IsDesktopProps } from '@/shared/styles/types';
import Modal from '@/shared/components/Modal';
import Terms from '@/components/terms';
import {
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import Button from '@/shared/components/Button';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  acceptTerms: () => void;
}

const TermsModal = ({ isVisible, onClose, acceptTerms }: Props) => {
  const isDesktop = useIsDesktop();
  const insets = useSafeAreaInsets();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const paddingToBottom = 20;
    const isCloseToBottom =
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom;

    if (isCloseToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
      acceptTerms();
    }
  };

  const handleCloseModal = () => {
    onClose();
    setHasScrolledToBottom(false);
  };

  const onAcceptPress = () => {
    acceptTerms();
    handleCloseModal();
  };

  return (
    <Modal isVisible={isVisible} setIsVisible={handleCloseModal}>
      <TermsModalContainer isDesktop={isDesktop}>
        <TermsModalHeader style={{ paddingTop: insets.top + 36 }}>
          <CloseButton
            onPress={handleCloseModal}
            style={{ top: isDesktop ? 20 : insets.top }}
          >
            <CloseIcon source={require('@/assets/images/CloseGreen.png')} />
          </CloseButton>
          <TermsModalTitle>review & accept terms</TermsModalTitle>
          <SubTitle>
            Please scroll down to read through the terms, select accept once you
            have finished.
          </SubTitle>
        </TermsModalHeader>
        <TermsScrollContainer
          onScroll={handleScroll}
          scrollEventThrottle={400}
          showsVerticalScrollIndicator={true}
        >
          <Terms isModal />
        </TermsScrollContainer>
        <ButtonContainer>
          <StyledButton
            text="Accept Terms"
            onPress={() => {
              onAcceptPress();
            }}
            disabled={!hasScrolledToBottom}
          />
        </ButtonContainer>
      </TermsModalContainer>
    </Modal>
  );
};

const TermsModalContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  backgroundColor: white,
  borderRadius: isDesktop ? 12 : 0,
  marginHorizontal: isDesktop ? 40 : 0,
  marginVertical: isDesktop ? 40 : 0,
  maxHeight: isDesktop ? '95%' : '100%',
  maxWidth: isDesktop ? 601 : '100%',
  overflow: 'hidden',
  ...BOX_SHADOW_STYLE,
}));

const TermsModalHeader = styled.View({
  alignItems: 'center',
  paddingHorizontal: 20,
  borderBottomWidth: 1,
  borderBottomColor: mint,
  backgroundColor: sand,
});

const SubTitle = styled(BodyText)({
  maxWidth: 324,
  textAlign: 'center',
  fontSize: 16,
  lineHeight: 18,
  color: lightGreen,
  marginBottom: 27,
});

const TermsModalTitle = styled(H3)({
  color: green,
  textTransform: 'uppercase',
  marginBottom: 8,
  textAlign: 'center',
});

const CloseButton = styled.TouchableOpacity({
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  right: 20,
});

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
});

const TermsScrollContainer = styled(ScrollView)({
  flex: 1,
});

const ButtonContainer = styled.View({
  padding: 16,
  alignItems: 'center',
  backgroundColor: white,
  borderTopWidth: 1,
  borderTopColor: mint,
});

const StyledButton = styled(Button)({
  width: '100%',
});

export default TermsModal;
